/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/passwordSettings.scss';

import React, {FocusEventHandler, MouseEventHandler, PureComponent} from 'react';
import {ChangeFunction, Loader} from '@dvrd/dvr-controls';
import {PasswordErrors} from "../../../util/interfaces";
import PasswordStrength from "../../password/passwordStrength";
import {BolmateButton, BolmatePasswordInput} from "../../controls";
import BolmateSwitch from "../../controls/switch/bolmateSwitch";
import InfoBanner from "../../misc/infoBanner";

interface Props {
    onChange: (key: 'new' | 'confirm' | 'pass') => ChangeFunction;
    onSubmit: MouseEventHandler;
    onResetError: (key: keyof PasswordErrors) => FocusEventHandler;
    onToggleTwoFactor: ChangeFunction;
    newPassword: string;
    confirmPassword: string;
    password: string;
    loading: boolean;
    errors: PasswordErrors;
    twoFactorEnabled: boolean;
}

export default class PasswordSettings extends PureComponent<Props> {
    renderFields = () => {
        const {password, newPassword, confirmPassword, onChange, onResetError, errors, loading} = this.props;

        return (
            <div className='fields'>
                <Loader active={loading}/>
                <BolmatePasswordInput
                    value={password}
                    onChange={onChange('pass')}
                    label='Huidig wachtwoord *'
                    error={errors.password}
                    onFocus={onResetError('password')}
                    inputProps={{placeholder: 'Huidig wachtwoord'}}
                />
                <BolmatePasswordInput
                    value={newPassword}
                    onChange={onChange('new')}
                    label='Nieuw wachtwoord *'
                    error={errors.newPassword}
                    onFocus={onResetError('newPassword')}
                    inputProps={{placeholder: 'Nieuw wachtwoord'}}
                />
                <PasswordStrength value={newPassword}/>
                <BolmatePasswordInput
                    value={confirmPassword}
                    onChange={onChange('confirm')}
                    label='Bevestig wachtwoord *'
                    error={errors.confirmPassword}
                    onFocus={onResetError('confirmPassword')}
                    inputProps={{placeholder: 'Bevestig wachtwoord'}}
                />
            </div>
        );
    };

    renderPassword = () => {
        const {onSubmit, loading} = this.props;

        return (
            <div className='change-password-container'>
                <div className='header-container'>
                    <label className='settings-title'>Wachtwoord wijzigen</label>
                    <label className='settings-sub'>
                        We raden sterk aan om iedere 60 dagen je wachtwoord te wijzigen
                    </label>
                </div>
                {this.renderFields()}
                <BolmateButton onClick={onSubmit} disabled={loading} label='Opslaan'/>
            </div>
        );
    };

    render2Fa = () => {
        const {twoFactorEnabled, onToggleTwoFactor} = this.props;

        return (
            <div className='two-factor-container'>
                <div className='header-container'>
                    <label className='settings-title'>Tweestapsverificatie</label>
                    <label className='settings-sub'>
                        Met tweestapsverificatie voeg je een extra beveiligingslaag toe aan je account.
                    </label>
                </div>
                <BolmateSwitch
                    value={twoFactorEnabled}
                    onChange={onToggleTwoFactor}
                    mainLabel='Tweestapsverificatie'
                    labels={['UIT', 'AAN']}
                />
            </div>
        );
    };

    render = () => {
        const {loading} = this.props;

        return (
            <div className='security-settings-page'>
                <InfoBanner
                    featureKey='security_settings'
                    variant='info'
                    title='Beveiliging'
                    message='We raden aan tweestapsverificatie in te schakelen. Je hoeft dan ook je e-mail niet meer te verifieren.'
                    showForNewUsersOnly={true}
                />

                <Loader active={loading}/>

                <div className='security-settings'>
                    {this.renderPassword()}
                    {this.render2Fa()}
                </div>
            </div>
        );
    };
}
